import { useSponsorExpandedAccess } from '@/layers/presentation/hooks/useSponsorExpandedAccess';
import { Program } from '@/types/models';
import {
  Button,
  Chip,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  useDisclosure,
} from '@nextui-org/react';
import React from 'react';

interface SponsorExpandedAccessTagProps {
  program: Program;
}

const SponsorExpandedAccessTag: React.FC<SponsorExpandedAccessTagProps> = ({ program }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { finalScore, evidence } = useSponsorExpandedAccess({ program });
  const scoreColor = finalScore >= 80 ? 'success' : finalScore >= 50 ? 'warning' : 'danger';

  const stopParentPress = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    if ('preventDefault' in e) {
      (e as any).preventDefault();
    }
  };

  const handleOpen = (e: React.SyntheticEvent) => {
    stopParentPress(e);
    onOpen();
  };

  return (
    <>
      <Chip
        size="sm"
        className="bg-default-200 text-default-700 cursor-pointer"
        onClick={handleOpen}
        onMouseDown={stopParentPress}
        onPointerDown={stopParentPress}
        onTouchStart={stopParentPress}
      >
        Sponsor score: {finalScore}
      </Chip>

      <Modal
        isOpen={isOpen}
        onOpenChange={onClose}
        placement="center"
        size="4xl"
        scrollBehavior="inside"
      >
        <ModalContent>
          <ModalHeader className="flex flex-col gap-1 text-2xl">Sponsor&apos;s Profile</ModalHeader>
          <ModalBody className="max-h-[70vh] overflow-y-auto">
            <div className="flex items-center gap-2">
              <span>Score:</span>
              <Chip color={scoreColor}>{finalScore}</Chip>
            </div>
            <div className="w-90 text-sm text-default-500">
              The higher the score, the more likely the sponsor is to provide expanded access, if no
              &quot;Expanded Access&quot; tag is present.
            </div>
            <div className="mt-4 space-y-3">
              {evidence.map(ev => (
                <div key={ev.label}>
                  <p className="font-medium">{ev.label}</p>
                  <ul className="list-disc list-outside space-y-1 pl-8">
                    {ev.sources.map(src => (
                      <li key={src.link} className="mt-4">
                        <a
                          href={src.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary underline"
                        >
                          {src.title || src.link}
                        </a>
                        {src.excerpt && <p className="text-sm text-gray-500">{src.excerpt}</p>}
                        {src.statement && <p className="text-sm text-gray-500">{src.statement}</p>}
                        {src.conditions && (
                          <p className="text-sm text-gray-500">{src.conditions}</p>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="primary" onPress={onClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default SponsorExpandedAccessTag;
